import { updateCheckout } from '@wix/ambassador-ecom-v1-checkout/http';
import { Checkout } from '@wix/ambassador-ecom-v1-checkout/types';
import { Transactions } from '../monitoring/transactions';
import { HttpError } from '@wix/http-client';
import {
  ApplicationStatus,
  ErrorData,
  ControllerFlowAPI,
} from '../types/types';
import { riseWixPluginErrorShownSrc35Evid202 } from '@wix/bi-logger-rise-ai-data/v2';
import {
  sitePluginId,
  sitePluginName,
} from '../components/RiseAppSitePlugin/consts';
import { getErrorTextFromGiftCardError } from '../utils/getErrorTextFromGiftCardError';
import { SPECS } from '../consts';

export interface UpdateCheckoutParams {
  checkout: Checkout;
  giftCardCode: string;
  currency: string;
}

export class UpdateCheckoutApi {
  private readonly flowAPI: ControllerFlowAPI;

  constructor({ flowAPI }: { flowAPI: ControllerFlowAPI }) {
    this.flowAPI = flowAPI;
  }

  async updateCheckout({
    checkout,
    giftCardCode,
    currency,
  }: UpdateCheckoutParams): Promise<Checkout> {
    const shouldUseErrorHandler = this.flowAPI.experiments?.enabled?.(
      SPECS.ShouldUseErrorHandler,
    );
    const shouldAddWixCurrencyHeader = this.flowAPI.experiments.enabled(
      SPECS.CheckPaymentCurrency,
    );

    try {
      this.flowAPI.panoramaClient
        ?.transaction(Transactions.ApplyStoreCredit)
        .start();
      const response = shouldUseErrorHandler
        ? await this.flowAPI.errorHandler.withErrorHandler(
            () =>
              this.flowAPI.httpClient.request((args) => ({
                ...updateCheckout({
                  checkout,
                  giftCardCode,
                })(args),
                ...(shouldAddWixCurrencyHeader && currency
                  ? { headers: { 'x-wix-currency': currency } }
                  : {}),
              })),
            {} as any,
          )
        : await this.flowAPI.httpClient.request((args) => ({
            ...updateCheckout({
              checkout,
              giftCardCode,
            })(args),
            ...(shouldAddWixCurrencyHeader && currency
              ? { headers: { 'x-wix-currency': currency } }
              : /* istanbul ignore next */ {}),
          }));
      this.flowAPI.panoramaClient
        ?.transaction(Transactions.ApplyStoreCredit)
        .finish();

      return response.data.checkout!;
    } catch (e) {
      if (shouldUseErrorHandler) {
        this.flowAPI.errorHandler.getResolvedError(e);
      }
      const error = e as HttpError;
      const giftCardError = error.response?.data as ErrorData;
      this.flowAPI.controllerConfig.setProps({
        status: ApplicationStatus.IDLE,
        giftCardError: giftCardError.details.applicationError?.code,
      });
      const errorKey = getErrorTextFromGiftCardError(
        giftCardError.details.applicationError?.code!,
      );
      this.flowAPI.bi?.report(
        riseWixPluginErrorShownSrc35Evid202({
          pluginId: sitePluginId,
          appName: sitePluginName,
          errorName: this.flowAPI.translations.t(errorKey),
        }),
      );

      return {};
    }
  }
}
